/**
 * "Same end project?" review queue for the Quoted Book.
 * The backend suggests likely cross-client contests (same end-client
 * RFP quoted through 2+ different clients); an admin confirms or
 * dismisses each one. Confirmed contests merge in the book maths and
 * the quarterly win-rate stats.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Loader2, RefreshCw, Swords } from "lucide-react";
import { useAuth } from "../../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtGbp = (v) =>
  v == null ? "-" : `£${Math.round(Number(v)).toLocaleString("en-GB")}`;

function SimChip({ label, pct }) {
  const tone =
    pct >= 60 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : pct >= 30 ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-stone-50 text-stone-500 border-stone-200";
  return (
    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${tone}`}>
      {label} {pct}%
    </span>
  );
}

function MemberRow({ m }) {
  const samples = Object.entries(m.samples || {})
    .map(([c, n]) => `${c} ${n}`)
    .join(" · ");
  return (
    <tr className="border-t border-stone-100" data-testid={`contest-member-${m.cid}`}>
      <td className="py-1.5 pr-3 text-xs font-medium text-slate-800 whitespace-nowrap">
        {m.client_company}
        <span className="ml-1.5 text-[10px] font-mono text-stone-400">#{m.cid}</span>
      </td>
      <td className="py-1.5 pr-3 text-[11px] text-stone-500 whitespace-nowrap">
        {m.proposal_date} ·{" "}
        <span className={m.status === "QUOTED" ? "text-[#1B4F8A] font-semibold" : ""}>{m.status}</span>
      </td>
      <td className="py-1.5 pr-3 text-[11px] text-stone-600 whitespace-nowrap">{fmtGbp(m.value_gbp)}</td>
      <td className="py-1.5 pr-3 text-[11px] text-stone-500">{(m.countries || []).join(", ") || "-"}</td>
      <td className="py-1.5 pr-3 text-[11px] text-stone-500">{samples || "-"}</td>
      <td className="py-1.5 text-[11px] text-stone-500">
        {(m.methodologies || []).join(", ") || "-"}
        {(m.audiences || []).length > 0 && (
          <span className="text-stone-400"> · {(m.audiences || []).join("; ")}</span>
        )}
      </td>
    </tr>
  );
}

function ContestCard({ c, isAdmin, busy, onDecide }) {
  const comp = c.evidence?.components || {};
  return (
    <div
      className="rounded-xl border border-stone-200 bg-white p-4"
      data-testid={`contest-card-${c.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-slate-900 capitalize">
            {c.topic}
            {c.live && (
              <span className="ml-2 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#1f7468]/10 text-[#1f7468] border border-[#1f7468]/25">
                live
              </span>
            )}
            <span
              className={`ml-2 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                c.evidence?.tier === "strong"
                  ? "bg-rose-50 text-rose-700 border-rose-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
            >
              {c.evidence?.tier === "strong" ? "strong match" : "possible match"} {c.evidence?.score_pct}%
            </span>
          </p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <SimChip label="specs" pct={comp.specs_pct || 0} />
            <SimChip label="audience" pct={comp.audience_pct || 0} />
            <SimChip label="method" pct={comp.methodology_pct || 0} />
            <SimChip label="markets" pct={comp.markets_pct || 0} />
            <SimChip label="samples" pct={comp.samples_pct || 0} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!isAdmin || busy}
            title={isAdmin ? "These quotes chase the same end project" : "Admin only"}
            onClick={() => onDecide(c.id, "confirm")}
            data-testid={`contest-confirm-${c.id}`}
            className="rounded-lg bg-[#1B4F8A] text-white text-xs px-3 py-1.5 hover:bg-[#163f6e] transition-colors disabled:opacity-40"
          >
            {busy === "confirm" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirm, same opportunity"}
          </button>
          <button
            type="button"
            disabled={!isAdmin || busy}
            title={isAdmin ? "Not the same end project" : "Admin only"}
            onClick={() => onDecide(c.id, "dismiss")}
            data-testid={`contest-dismiss-${c.id}`}
            className="rounded-lg border border-stone-300 text-stone-600 text-xs px-3 py-1.5 hover:border-stone-400 transition-colors disabled:opacity-40"
          >
            {busy === "dismiss" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Dismiss, unrelated"}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-left">
          <thead>
            <tr className="text-[9px] font-mono uppercase tracking-widest text-stone-400">
              <th className="pr-3 font-normal">Client</th>
              <th className="pr-3 font-normal">Quoted</th>
              <th className="pr-3 font-normal">Value</th>
              <th className="pr-3 font-normal">Markets</th>
              <th className="pr-3 font-normal">Samples</th>
              <th className="font-normal">Method · audience</th>
            </tr>
          </thead>
          <tbody>
            {(c.members || []).map((m) => (
              <MemberRow key={m.cid} m={m} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ContestReview({ onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState({}); // id -> "confirm"|"dismiss"
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/contests?status=suggested`);
      setItems(data.contests || []);
      setCounts(data.counts || {});
    } catch {
      /* panel is best-effort */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setBusy((b) => ({ ...b, [id]: action }));
    try {
      await axios.post(`${API}/api/contests/${id}/${action}`);
      toast.success(action === "confirm" ? "Confirmed: counted as one opportunity." : "Dismissed.");
      await load();
      if (action === "confirm" && typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the decision.");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const runDetection = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/api/contests/refresh`);
      toast.message("Detection running in the background, refresh in a minute.");
      setTimeout(load, 45000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start detection.");
    } finally {
      setRefreshing(false);
    }
  };

  if (!items.length && !counts.confirmed) return null;

  return (
    <div
      className="mx-5 mt-4 rounded-2xl border border-amber-200 bg-amber-50/60"
      data-testid="contest-review-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="contest-review-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm text-amber-900">
          <Swords className="w-4 h-4" />
          <strong>Same end project?</strong>
          {items.length > 0 ? (
            <span data-testid="contest-pending-count">
              {items.length} suggested contest{items.length === 1 ? "" : "s"} awaiting review
            </span>
          ) : (
            <span>No suggestions pending{counts.confirmed ? ` · ${counts.confirmed} confirmed` : ""}</span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-amber-700" /> : <ChevronDown className="w-4 h-4 text-amber-700" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-[11px] text-amber-800/80 -mt-1">
            These look like the same end-client project quoted through different clients. Only one
            can ever convert. Confirming counts the group as ONE opportunity in the book and the
            quarterly win rate; each client keeps its own track record.
            {!isAdmin && " Confirmation is admin only."}
          </p>
          {items.map((c) => (
            <ContestCard key={c.id} c={c} isAdmin={isAdmin} busy={busy[c.id]} onDecide={decide} />
          ))}
          {isAdmin && (
            <button
              type="button"
              onClick={runDetection}
              disabled={refreshing}
              data-testid="contest-refresh-btn"
              className="inline-flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-950 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Re-run detection now
            </button>
          )}
        </div>
      )}
    </div>
  );
}
